import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export function FooterNewsletterSignup() {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Ange din e-postadress.");
      return;
    }
    if (!consent) {
      setError("Du måste godkänna villkoren för att prenumerera.");
      return;
    }
    setLoading(true);
    try {
      const { error: fnError } = await supabase.functions.invoke("subscribe-newsletter", {
        body: {
          email: trimmed,
          full_name: honeypot,
        },
      });

      if (fnError) {
        console.error("[Newsletter]", fnError);
        setError("Något gick fel. Försök igen senare.");
        return;
      }

      setSuccess(true);
      setEmail("");
      setConsent(false);
    } catch (err) {
      console.error("[Newsletter]", err);
      setError("Något gick fel. Försök igen senare.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full space-y-4">
        <p className="font-serif text-2xl sm:text-3xl font-bold text-[#111] leading-tight">
          Tack för din anmälan!
        </p>
        <p className="text-[#555] text-sm leading-relaxed" role="status">
          Du får snart bröllopsinspiration i din inkorg.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <h2 className="font-serif text-2xl sm:text-3xl font-normal text-[#111] leading-tight">
        Bröllopsinspiration <em className="italic">direkt</em> i din inkorg
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Honeypot — hidden from humans */}
        <div className="hidden" aria-hidden="true" style={{ display: "none" }}>
          <input
            type="text"
            name="full_name"
            autoComplete="off"
            tabIndex={-1}
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="relative">
          <label htmlFor="footer-newsletter-email" className="sr-only">
            E-postadress
          </label>
          <div className="relative flex items-end border-b border-black pb-1 focus-within:border-[#26333b]">
            <input
              id="footer-newsletter-email"
              type="email"
              name="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Ange e-postadress"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              className={cn(
                "w-full bg-transparent border-0 px-0 py-2 pr-10 text-[#333] placeholder:text-[#777]",
                "text-base outline-none focus:ring-0 disabled:opacity-60",
              )}
              aria-invalid={!!error}
              aria-describedby={error ? "footer-newsletter-error" : undefined}
            />
            <Mail
              className="absolute right-0 bottom-2 h-5 w-5 shrink-0 text-[#2d6b6b]"
              aria-hidden
            />
          </div>
        </div>

        <div className="flex gap-3 items-start">
          <Checkbox
            id="footer-newsletter-consent"
            checked={consent}
            onCheckedChange={(v) => setConsent(v === true)}
            disabled={loading}
            className="mt-0.5 border-black rounded-sm"
          />
          <Label
            htmlFor="footer-newsletter-consent"
            className="text-[13px] leading-snug text-[#555] font-normal cursor-pointer"
          >
            Jag bekräftar att jag vill prenumerera på Weddinglys nyhetsbrev och att Weddingly har rätt att lagra min
            e-postadress.{" "}
            <Link
              to="/privacy-policy"
              className="underline underline-offset-2 text-[#333] hover:text-[#111]"
            >
              Läs Weddinglys integritetspolicy här.
            </Link>
          </Label>
        </div>

        <div className="space-y-3">
          <button
            type="submit"
            disabled={loading}
            className={cn(
              "inline-flex min-w-[200px] items-center justify-center gap-2 bg-black px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white",
              "transition-opacity hover:opacity-90 disabled:opacity-50 rounded-none",
            )}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                Skickar...
              </>
            ) : (
              "Prenumerera"
            )}
          </button>

          <p className="text-sm italic text-[#666] leading-relaxed">
            Ta del av tips, guider och massor av bröllopsinspiration!
          </p>
        </div>

        {error ? (
          <p id="footer-newsletter-error" className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </div>
  );
}
